import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import DocumentViewer from '../components/DocumentViewer';
import { documentAPI, caseAPI, fileUtils } from '../utils/api';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  Filter,
  Search,
  ChevronRight,
  Loader2,
  Download,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
};

export default function OfficerDocuments() {
  const [documents, setDocuments] = useState([]);
  const [cases, setCases] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingDocument, setViewingDocument] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      
      // Fetch all cases assigned to this officer
      const casesRes = await caseAPI.getMyCases();
      const myCases = casesRes.data || [];
      
      // Create a map of case IDs to case data for quick lookup
      const caseMap = {};
      myCases.forEach(c => {
        caseMap[c.id] = c;
      });
      setCases(caseMap);
      
      // Fetch documents for all cases
      const allDocs = [];
      for (const caseItem of myCases) {
        try {
          const docsRes = await documentAPI.getByCaseId(caseItem.id);
          const caseDocs = (docsRes.data || []).map(doc => ({
            ...doc,
            case_id: caseItem.id,
            client_name: caseItem.client_name || 'Unknown Client',
            visa_type: caseItem.visa_type || 'Visa'
          }));
          allDocs.push(...caseDocs);
        } catch (err) {
          console.log(`No documents for case ${caseItem.id}`);
        }
      }
      
      // Sort by upload date (most recent first)
      allDocs.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
      
      setDocuments(allDocs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    // Status filter
    if (filter !== 'all' && doc.status !== filter) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        doc.checklist_item_name?.toLowerCase().includes(query) ||
        doc.client_name?.toLowerCase().includes(query) ||
        doc.case_id?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const getStatusCounts = () => {
    const counts = { all: documents.length, pending: 0, approved: 0, rejected: 0 };
    documents.forEach(doc => {
      if (counts[doc.status] !== undefined) {
        counts[doc.status]++;
      }
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  const handleViewCase = (caseId) => {
    navigate(`/officer/cases/${caseId}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content bg-slate-50">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Document Review</h1>
            <p className="text-slate-500">Review and manage client documents across all your cases</p>
          </div>
          <button 
            onClick={fetchDocuments}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-all flex items-center gap-2"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { key: 'all', label: 'All Documents' },
            { key: 'pending', label: 'Pending Review' },
            { key: 'approved', label: 'Approved' },
            { key: 'rejected', label: 'Rejected' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                filter === tab.key 
                  ? 'bg-teal-500 text-white' 
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {tab.label}
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                filter === tab.key ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {statusCounts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search by document name, client, or case..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none bg-white"
            />
          </div>
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No documents found</h3>
            <p className="text-slate-500">
              {filter === 'pending' 
                ? 'No documents pending review at this time.' 
                : 'No documents match your current filters.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Document</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Client / Case</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Uploaded</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Status</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDocuments.map((doc) => {
                  const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr key={doc.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            <FileText className="text-slate-500" size={20} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{doc.checklist_item_name}</p>
                            <p className="text-sm text-slate-500">{doc.file_type || 'PDF'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900">{doc.client_name}</p>
                        <p className="text-sm text-slate-500">{doc.visa_type} • Case #{doc.case_id.slice(0, 8)}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-600">{formatDate(doc.uploaded_at)}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.color}`}>
                          <StatusIcon size={14} />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {doc.file_url && (
                            <>
                              <button 
                                onClick={() => setViewingDocument(doc)}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                title="View Document"
                                data-testid={`view-doc-${doc.id}`}
                              >
                                <Eye size={18} />
                              </button>
                              <button 
                                onClick={() => fileUtils.downloadFile(doc.file_url, doc.file_name)}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Download Document"
                                data-testid={`download-doc-${doc.id}`}
                              >
                                <Download size={18} />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleViewCase(doc.case_id)}
                            className="px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-100 transition-all flex items-center gap-1"
                          >
                            Review
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Footer */}
        {!loading && filteredDocuments.length > 0 && (
          <div className="mt-4 text-sm text-slate-500">
            Showing {filteredDocuments.length} of {documents.length} total documents
          </div>
        )}

        {/* Document Viewer Modal */}
        {viewingDocument && (
          <DocumentViewer
            fileUrl={viewingDocument.file_url}
            fileName={viewingDocument.file_name}
            onClose={() => setViewingDocument(null)}
          />
        )}
      </div>
    </div>
  );
}
